import { getNavItemByPath } from './navConfig';

const NAV_RECENTS_KEY = 'engine-nav-recents';
const MAX_RECENT = 3;

/** Default pinned shortcuts on first visit (no saved nav settings). */
export const DEFAULT_PINNED_NAV_PATHS = ['/subscribers', '/outgoing-newsletters'] as const;

export interface NavRecentsState {
  /** Most recent canonical nav paths (max MAX_RECENT), excluding pin bookkeeping */
  recent: string[];
  /** Pinned canonical nav paths — always shown in shortcuts */
  pinned: string[];
}

function defaultState(): NavRecentsState {
  return { recent: [], pinned: [...DEFAULT_PINNED_NAV_PATHS] };
}

export function readNavRecents(): NavRecentsState {
  try {
    const stored = localStorage.getItem(NAV_RECENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as NavRecentsState;
      return sanitize({
        recent: Array.isArray(parsed.recent) ? parsed.recent : [],
        pinned: Array.isArray(parsed.pinned) ? parsed.pinned : [],
      });
    }
  } catch {
    /* ignore */
  }
  const defaults = sanitize(defaultState());
  persistNavRecents(defaults);
  return defaults;
}

export function persistNavRecents(state: NavRecentsState): void {
  try {
    localStorage.setItem(NAV_RECENTS_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** Drop paths that no longer exist in nav config. */
function sanitize(state: NavRecentsState): NavRecentsState {
  return {
    recent: state.recent.filter((p) => getNavItemByPath(p)),
    pinned: state.pinned.filter((p) => getNavItemByPath(p)),
  };
}

export function recordNavVisit(canonicalPath: string): NavRecentsState {
  if (!getNavItemByPath(canonicalPath)) return readNavRecents();

  const state = sanitize(readNavRecents());
  const recent = [
    canonicalPath,
    ...state.recent.filter((p) => p !== canonicalPath),
  ].slice(0, MAX_RECENT);

  const next = { ...state, recent };
  persistNavRecents(next);
  return next;
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function reorderNavPins(fromIndex: number, toIndex: number): NavRecentsState {
  const state = sanitize(readNavRecents());
  const pinned = moveItem(state.pinned, fromIndex, toIndex);
  const next = { ...state, pinned };
  persistNavRecents(next);
  return next;
}

export function toggleNavPin(canonicalPath: string): NavRecentsState {
  if (!getNavItemByPath(canonicalPath)) return readNavRecents();

  const state = sanitize(readNavRecents());
  const pinned = state.pinned.includes(canonicalPath)
    ? state.pinned.filter((p) => p !== canonicalPath)
    : [...state.pinned, canonicalPath];

  const next = { ...state, pinned };
  persistNavRecents(next);
  return next;
}

/** Pinned first, then up to MAX_RECENT recent items not already pinned. */
export function getShortcutPaths(state: NavRecentsState): {
  pinned: string[];
  recent: string[];
} {
  const clean = sanitize(state);
  const pinned = clean.pinned;
  const recent = clean.recent.filter((p) => !pinned.includes(p)).slice(0, MAX_RECENT);
  return { pinned, recent };
}
